import api from "../../Utils/api";
import React, { useRef, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import PasswordResetDialog from "./ResetPassword";
import Spinner from "../Spinner";
import LocationSelector from "./LocationSelector";
import Select from "react-select";

// Helper: default user data
const defaultUser = {
  id: null,
  firstName: "",
  lastName: "",
  preferredName: "",
  pronouns: "",
  birthday: "",
  email: "",
  phone: "",
  address: "",
  postCode: "",
  city: "",
  state: "",
  notes: "",
  country: "",
  emergencyName: "",
  emergencyPhone: "",
  kioskPin: "1234",
  profileImage: null, // File or data URL
  // Work details
  deputyAccessLevel: "system_administrator",
  color: "",
  locations: {
    primaryLocation: "",
    subLocations: [],
  },
  hiredOn: "",
  // Automatic break fields
  automaticBreak: false,
  weeklyHours: "",
  automaticBreakMinutes: "",
  // Daily hours
  mondayWorkHours: "",
  tuesdayWorkHours: "",
  wednesdayWorkHours: "",
  thursdayWorkHours: "",
  fridayWorkHours: "",
  saturdayWorkHours: "",
  sundayWorkHours: "",
};

export default function PersonalDetailsDialog({
  id,
  open,
  onClose,
  initialUser = null,
  onSave,
  onDelete,
  locations = [],
}) {
  const currentUser = useSelector((state) => state.user.user);
  const [user, setUser] = useState(initialUser || defaultUser);
  const [editing, setEditing] = useState(!initialUser); // If no user, it's create mode
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Section refs for scrolling
  const personalRef = useRef(null);
  const contactRef = useRef(null);
  const loginRef = useRef(null);
  const workRef = useRef(null);
  const documentsRef = useRef(null);
  const scrollAreaRef = useRef(null);

  const [activeTab, setActiveTab] = useState("personal");

  // Document management state
  const [documents, setDocuments] = useState([]);
  const [documentName, setDocumentName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  // Prevent background scroll
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  useEffect(() => {
    if (id !== "") {
      getUserProfile();
      getDocuments();
    }
  }, [id]);

  useEffect(() => {
    if (currentUser && currentUser.deputyAccessLevel === "system_administrator") {
      fetchCompanies();
    }
  }, [currentUser]);

  useEffect(() => {
    if (user && user.companyID) {
      // Find and set selected company
      const company = companies.find((c) => c._id === user.companyID);
      if (company) {
        setSelectedCompany(company);
      }
    } else {
      setSelectedCompany(null);
    }
  }, [user, companies]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get("/company");
      if (response.data.success) {
        // Only show active (non-archived) companies
        setCompanies((response.data.companies || []).filter(company => !company.archive));
      }
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  };

  const getUserProfile = async () => {
    setIsLoading(true);
    const res = await api.get(`/user/${id}`);
    setIsLoading(false);
    if (res.data.success) {
      setUser(res.data.user);
    } else {
      console.error("Failed to fetch user profile");
    }
  };

  const getDocuments = async () => {
    if (!id) return;
    try {
      const response = await api.post("/document/list", { userID: id });
      if (response.data.success) {
        setDocuments(response.data.documents || []);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  const handleDocumentUpload = async () => {
    if (!documentName.trim() || !selectedFile || !id) {
      toast.error("Please provide document name and select a file");
      return;
    }

    const formData = new FormData();
    formData.append("document_name", documentName);
    formData.append("userID", id);
    formData.append("file", selectedFile);

    try {
      setIsLoading(true);
      const response = await api.post("/document/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setIsLoading(false);

      if (response.data.success) {
        toast.success("Document uploaded successfully!");
        setDocumentName("");
        setSelectedFile(null);
        const fileInput = document.getElementById("documentFile");
        if (fileInput) fileInput.value = "";
        getDocuments();
      } else {
        toast.error(response.data.message || "Failed to upload document");
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error("Failed to upload document");
      setIsLoading(false);
    }
  };

  const handleDeleteDocument = async (documentID) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      const response = await api.post("/document/delete", { documentID });
      if (response.data.success) {
        toast.success("Document deleted successfully!");
        getDocuments();
      } else {
        toast.error(response.data.message || "Failed to delete document");
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Failed to delete document");
    }
  };

  // Scroll to section
  const scrollToSection = (ref, tab) => {
    setActiveTab(tab);
    if (ref.current && scrollAreaRef.current) {
      const scrollArea = scrollAreaRef.current;
      const sectionTop = ref.current.offsetTop - 280;
      scrollArea.scrollTo({ top: sectionTop, behavior: "smooth" });
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((u) => ({ ...u, [name]: value }));
  };

  // Handle save
  const handleSave = async () => {
    let requestBody = {
      _id: user._id,
      updatedData: user,
    };

    setIsLoading(true);
    const res = await api.post("/user/update", requestBody);
    setIsLoading(false);
    if (res.data.success) {
      toast.success("Profile updated successfully!");
      setEditing(false);
      if (onSave) onSave(defaultUser);
    } else {
      toast.error("Failed to update profile: " + res.data.message);
    }
  };

  // Handle delete
  const handleDelete = () => {
    if (window.confirm("Delete this profile?") && onDelete) {
      onDelete(user.id);
      onClose();
    }
  };

  // Handle create new
  const handleCreateNew = () => {
    // setUser(defaultUser);
    // setEditing(true);
    // setImagePreview(null);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
      {isLoading && <Spinner />}
      <div className="bg-white w-full max-w-5xl max-h-[90vh] flex flex-col rounded-lg">
        {/* Header */}
        <div className="p-6 flex justify-between items-start">
          <div>
            <div>
              <h1 className="text-3xl font-bold mb-1">{"Personal"}</h1>
              <div className="text-lg text-gray-600">{user.preferredName}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-6 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              Back
            </button>
            <button
              onClick={editing ? handleSave : () => setEditing(true)}
              className="px-6 py-2 rounded bg-gray-600 text-white hover:bg-gray-900"
            >
              {editing ? "Save" : "Edit"}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-gray-200">
          <div className="flex space-x-8">
            <button
              className={`py-2 font-medium border-b-2 transition-colors duration-200 ${
                activeTab === "personal"
                  ? "border-gray-600 text-gray-600"
                  : "border-transparent text-gray-600 hover:text-gray-600"
              }`}
              onClick={() => scrollToSection(personalRef, "personal")}
              type="button"
            >
              Personal details
            </button>
            <button
              className={`py-2 font-medium border-b-2 transition-colors duration-200 ${
                activeTab === "contact"
                  ? "border-gray-600 text-gray-600"
                  : "border-transparent text-gray-600 hover:text-gray-600"
              }`}
              onClick={() => scrollToSection(contactRef, "contact")}
              type="button"
            >
              Contact
            </button>
            <button
              className={`py-2 font-medium border-b-2 transition-colors duration-200 ${
                activeTab === "work"
                  ? "border-gray-600 text-gray-600"
                  : "border-transparent text-gray-600 hover:text-gray-600"
              }`}
              onClick={() => scrollToSection(workRef, "work")}
              type="button"
            >
              Work details
            </button>
            <button
              className={`py-2 font-medium border-b-2 transition-colors duration-200 ${
                activeTab === "documents"
                  ? "border-gray-600 text-gray-600"
                  : "border-transparent text-gray-600 hover:text-gray-600"
              }`}
              onClick={() => scrollToSection(documentsRef, "documents")}
              type="button"
            >
              Documents
            </button>
            <button
              className={`py-2 font-medium border-b-2 transition-colors duration-200 ${
                activeTab === "login"
                  ? "border-gray-600 text-gray-600"
                  : "border-transparent text-gray-600 hover:text-gray-600"
              }`}
              onClick={() => scrollToSection(loginRef, "login")}
              type="button"
            >
              Login information
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div
          ref={scrollAreaRef}
          className="overflow-y-auto  flex-1 p-6 space-y-16"
          style={{ scrollBehavior: "smooth" }}
        >
          {/* Personal Details */}
          <section ref={personalRef}>
            <h2 className="text-2xl font-semibold mb-8">Personal details</h2>
            {/* <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Profile Picture</p>
              </div>
              <div className="col-span-2">
                <div className="flex flex-wrap items-center mr-10">
                  <div className="w-16 h-16 rounded-full mr-4 bg-gray-100 flex items-center justify-center text-2xl font-semibold text-gray-700 overflow-hidden">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Profile"
                        className="object-cover w-full h-full rounded-full"
                      />
                    ) : (
                      (user.firstName?.[0] || "") + (user.lastName?.[0] || "")
                    )}
                  </div>
                  <div>
                    {editing && (
                      <>
                        <label className="text-sm text-gray-800 bg-gray-100 px-3 py-1 rounded mb-1 cursor-pointer">
                          Upload photo
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleImageChange}
                          />
                        </label>
                        <div className="text-xs text-gray-500">
                          Accepted: JPG, JPEG, PNG. Max size 10MB
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div> */}
            {/* First Name */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">First Name</p>
              </div>
              <div className="col-span-2">
                <input
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  name="firstName"
                  value={user.firstName}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </div>
            </div>
            {/* Last Name */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Last Name</p>
              </div>
              <div className="col-span-2">
                <input
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  name="lastName"
                  value={user.lastName}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </div>
            </div>
            {/* Full Name */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Preferred full name</p>
                <p className="text-xs text-gray-500 mt-1">
                  Their display name in Deputy.
                </p>
              </div>
              <div className="col-span-2">
                <input
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  name="preferredName"
                  value={user.preferredName}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </div>
            </div>
            {/* Pronouns */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Pronouns</p>
                <p className="text-xs text-gray-500 mt-1">
                  This will appear next to a team member's name.
                </p>
              </div>
              <div className="col-span-2">
                <select
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  name="pronouns"
                  value={user.pronouns}
                  onChange={handleChange}
                  disabled={!editing}
                >
                  <option value="">Not Specified</option>
                  <option value="They/Them">They / Them</option>
                  <option value="She/Her">She / Her</option>
                  <option value="He/Him">He / Him</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>
            </div>
            {/* DOB */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Date of birth</p>
              </div>
              <div className="col-span-2">
                <input
                  type="date"
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  name="birthday"
                  value={user.birthday}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section ref={contactRef}>
            <h2 className="text-2xl font-semibold mb-8">Contact</h2>
            <div className="space-y-5">
              {/* Email */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">Email</p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="email"
                    value={user.email}
                    disabled
                    onChange={handleChange}
                  />
                </div>
              </div>
              {/* Mobile */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">Mobile</p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="phone"
                    value={user.phone}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
              {/* Address */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">Address</p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="address"
                    value={user.address}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
              {/* Postcode */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">Postcode</p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="postCode"
                    value={user.postCode}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
              {/* City */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">City</p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="city"
                    value={user.city}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
              {/* State */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">State</p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="state"
                    value={user.state}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
              {/* Country */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">Country</p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="country"
                    value={user.country}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
              {/* Emergency Contact Name */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">
                    Emergency contact name
                  </p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="emergencyName"
                    value={user.emergencyName}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
              {/* Emergency Phone */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">
                    Emergency phone number
                  </p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="emergencyPhone"
                    value={user.emergencyPhone}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>

              {/* Emergency Phone */}
              <div className="grid grid-cols-5 items-start mb-2">
                <div>
                  <p className="font-semibold text-sm">Notes</p>
                </div>
                <div className="col-span-2">
                  <textarea
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="notes"
                    rows={4}
                    value={user.notes}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Work Details Section */}
          <section ref={workRef}>
            <h2 className="text-2xl font-semibold mb-8">Work details</h2>
            {/* Color */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Color</p>
              </div>
              <div className="col-span-2">
                <input
                  type="color"
                  name="color"
                  value={user.color || "#000000"}
                  onChange={handleChange}
                  disabled={!editing}
                  className="h-10 w-20 p-0 border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed"
                />
              </div>
            </div>
            {/* Access Level */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Access level</p>
                <p className="text-xs text-gray-500 mt-1">
                  A set of permissions that control what a team member can do in Deputy.
                </p>
              </div>
              <div className="col-span-2">
                <select
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  name="deputyAccessLevel"
                  value={user.deputyAccessLevel}
                  onChange={handleChange}
                  disabled={!editing}
                >
                  <option value="system_administrator">System Administrator</option>
                  <option value="company_manager">Company Manager</option>
                  <option value="employee">Employee</option>
                </select>
              </div>
            </div>
            {/* Company - Only for system administrators */}
            {currentUser && currentUser.deputyAccessLevel === "system_administrator" && (
              <div className="grid grid-cols-5 mb-8">
                <div>
                  <p className="font-semibold text-sm">Company</p>
                  <p className="text-xs text-gray-500 mt-1">
                    The company this staff member belongs to.
                  </p>
                </div>
                <div className="col-span-2">
                  <Select
                    name="company"
                    placeholder="Select a Company"
                    className="basic-multi-select w-full cursor-pointer"
                    classNamePrefix="select"
                    options={companies}
                    value={selectedCompany}
                    onChange={(option) => {
                      setSelectedCompany(option);
                      setUser((u) => ({
                        ...u,
                        companyID: option ? option._id : null,
                      }));
                    }}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option._id}
                    isClearable={true}
                    isDisabled={!editing}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: "#d1d5db",
                        boxShadow: "none",
                        "&:hover": {
                          borderColor: "#9ca3af",
                        },
                      }),
                    }}
                  />
                </div>
              </div>
            )}
            {/* Works at */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Works at</p>
                <p className="text-xs text-gray-500 mt-1">
                  Locations where a team member can be scheduled.
                </p>
              </div>
              <div className="col-span-2">
                <LocationSelector
                  value={user.locations || { primaryLocation: "", subLocations: [] }}
                  locations={locations.map((item) => ({
                    name: item.name,
                    _id: item._id,
                  }))}
                  onChange={(newLocations) =>
                    setUser((prev) => ({ ...prev, locations: newLocations }))
                  }
                  disabled={!editing}
                />
              </div>
            </div>
            {/* Hired on */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Hired on</p>
              </div>
              <div className="col-span-2">
                <input
                  type="date"
                  name="hiredOn"
                  value={user.hiredOn?.slice(0, 10) || ""}
                  onChange={handleChange}
                  disabled={!editing}
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                />
              </div>
            </div>
            {/* Weekly Hours */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Weekly hours</p>
                <p className="text-xs text-gray-500 mt-1">
                  Total contracted hours per week.
                </p>
              </div>
              <div className="col-span-2">
                <input
                  type="number"
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  name="weeklyHours"
                  value={user.weeklyHours || ""}
                  onChange={handleChange}
                  disabled={!editing}
                  min="0"
                  step="0.5"
                  placeholder="e.g., 40"
                />
              </div>
            </div>
            {/* Automatic Break */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Automatic break</p>
                <p className="text-xs text-gray-500 mt-1">
                  Automatically add break times to shifts.
                </p>
              </div>
              <div className="col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="automaticBreak"
                    checked={user.automaticBreak || false}
                    onChange={(e) =>
                      setUser((prev) => ({ ...prev, automaticBreak: e.target.checked }))
                    }
                    disabled={!editing}
                    className="mr-2 h-4 w-4 accent-gray-600"
                  />
                  <span className="text-sm text-gray-700">
                    {user.automaticBreak ? "Enabled" : "Disabled"}
                  </span>
                </label>
              </div>
            </div>
            {/* Automatic Break Minutes - only show if automatic break is enabled */}
            {user.automaticBreak && (
              <div className="grid grid-cols-5 mb-8">
                <div>
                  <p className="font-semibold text-sm">Automatic break minutes</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Minutes deducted automatically from shifts.
                  </p>
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="automaticBreakMinutes"
                    value={user.automaticBreakMinutes || ""}
                    onChange={handleChange}
                    disabled={!editing}
                    min="0"
                    step="1"
                    placeholder="e.g., 30"
                  />
                </div>
              </div>
            )}
            {/* Daily Hours Section */}
            <div className="mt-8 mb-4">
              <h3 className="text-lg font-semibold mb-4">Daily contracted hours</h3>
              {/* Monday */}
              <div className="grid grid-cols-5 mb-4">
                <div>
                  <p className="font-semibold text-sm">Monday</p>
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="mondayWorkHours"
                    value={user.mondayWorkHours || ""}
                    onChange={handleChange}
                    disabled={!editing}
                    min="0"
                    step="0.5"
                    placeholder="e.g., 8"
                  />
                </div>
              </div>
              {/* Tuesday */}
              <div className="grid grid-cols-5 mb-4">
                <div>
                  <p className="font-semibold text-sm">Tuesday</p>
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="tuesdayWorkHours"
                    value={user.tuesdayWorkHours || ""}
                    onChange={handleChange}
                    disabled={!editing}
                    min="0"
                    step="0.5"
                    placeholder="e.g., 8"
                  />
                </div>
              </div>
              {/* Wednesday */}
              <div className="grid grid-cols-5 mb-4">
                <div>
                  <p className="font-semibold text-sm">Wednesday</p>
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="wednesdayWorkHours"
                    value={user.wednesdayWorkHours || ""}
                    onChange={handleChange}
                    disabled={!editing}
                    min="0"
                    step="0.5"
                    placeholder="e.g., 8"
                  />
                </div>
              </div>
              {/* Thursday */}
              <div className="grid grid-cols-5 mb-4">
                <div>
                  <p className="font-semibold text-sm">Thursday</p>
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="thursdayWorkHours"
                    value={user.thursdayWorkHours || ""}
                    onChange={handleChange}
                    disabled={!editing}
                    min="0"
                    step="0.5"
                    placeholder="e.g., 8"
                  />
                </div>
              </div>
              {/* Friday */}
              <div className="grid grid-cols-5 mb-4">
                <div>
                  <p className="font-semibold text-sm">Friday</p>
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="fridayWorkHours"
                    value={user.fridayWorkHours || ""}
                    onChange={handleChange}
                    disabled={!editing}
                    min="0"
                    step="0.5"
                    placeholder="e.g., 8"
                  />
                </div>
              </div>
              {/* Saturday */}
              <div className="grid grid-cols-5 mb-4">
                <div>
                  <p className="font-semibold text-sm">Saturday</p>
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="saturdayWorkHours"
                    value={user.saturdayWorkHours || ""}
                    onChange={handleChange}
                    disabled={!editing}
                    min="0"
                    step="0.5"
                    placeholder="e.g., 0"
                  />
                </div>
              </div>
              {/* Sunday */}
              <div className="grid grid-cols-5 mb-4">
                <div>
                  <p className="font-semibold text-sm">Sunday</p>
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="sundayWorkHours"
                    value={user.sundayWorkHours || ""}
                    onChange={handleChange}
                    disabled={!editing}
                    min="0"
                    step="0.5"
                    placeholder="e.g., 0"
                  />
                </div>
              </div>
            </div>
          </section>
          
          {/* Documents Section */}
          <section ref={documentsRef}>
            <h2 className="text-2xl font-semibold mb-8">Documents</h2>
            
            {/* Upload Document Form */}
            {editing && (
              <div className="mb-8 p-4 border border-gray-300 rounded-lg bg-gray-50">
                <div className="flex items-end gap-4">
                  {/* Document Name */}
                  <div className="flex-1">
                    <label className="block text-sm font-semibold mb-1">Document name:</label>
                    <input
                      type="text"
                      className="w-full rounded border-gray-300 bg-white p-2"
                      value={documentName}
                      onChange={(e) => setDocumentName(e.target.value)}
                      placeholder="Enter document name"
                    />
                  </div>
                  
                  {/* File Input */}
                  <div className="flex-1">
                    <label className="block text-sm font-semibold mb-1">File:</label>
                    <input
                      type="file"
                      id="documentFile"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setSelectedFile(file);
                          if (!documentName) {
                            const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
                            setDocumentName(fileNameWithoutExt);
                          }
                        }
                      }}
                    />
                    <div className="flex items-center border border-gray-300 rounded h-[38px] bg-white">
                      <button
                        type="button"
                        className="px-3 py-2 bg-gray-100 border-r border-gray-300 text-sm hover:bg-gray-200 rounded-l"
                        onClick={() => document.getElementById('documentFile').click()}
                      >
                        Choose File
                      </button>
                      <span className="px-3 text-sm text-gray-500 flex-1 truncate">
                        {selectedFile ? selectedFile.name : 'No file chosen'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Upload Button */}
                  <div>
                    <button
                      onClick={handleDocumentUpload}
                      disabled={!documentName.trim() || !selectedFile}
                      className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition disabled:opacity-50 disabled:cursor-not-allowed h-[38px]"
                    >
                      Upload
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Documents List */}
            <div className="border border-gray-200 rounded">
              <div className="bg-gray-50 p-2 border-b border-gray-200">
                <div className="grid grid-cols-5 gap-2 text-sm font-medium">
                  <div>ID</div>
                  <div className="col-span-2 text-left">Document Name</div>
                  <div className="col-span-1 text-left">File</div>
                  {editing && <div className="col-span-1 text-left">Actions</div>}
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto">
                {documents.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No documents uploaded yet
                  </div>
                ) : (
                  documents.map((doc, index) => (
                    <div key={doc._id} className="grid grid-cols-5 gap-2 p-2 border-b border-gray-100 hover:bg-gray-50">
                      <div className="text-sm">{index + 1}</div>
                      <div className="text-sm text-left col-span-2">{doc.document_name}</div>
                      <div className="text-sm text-left col-span-1">
                        <a
                          href={`${process.env.REACT_APP_SERVER_ADDRESS || "http://localhost:8001"}/api/document/file/${doc.file_name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700 underline"
                        >
                          View
                        </a>
                      </div>
                      {editing && (
                        <div className="text-sm col-span-1">
                          <button
                            onClick={() => handleDeleteDocument(doc._id)}
                            className="text-red-500 hover:text-red-700 underline"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>

          {/* Login Information Section */}
          <section ref={loginRef}>
            <h2 className="text-2xl font-semibold mb-8">Login information</h2>
            <div className="grid grid-cols-5 items-center mb-8">
              <div>
                <p className="font-semibold text-sm">Reset Password</p>
              </div>
              <div className="col-span-2">
                <PasswordResetDialog id={id} />
              </div>
            </div>
          </section>
        </div>
        {/* Create New Button (for demo) */}
        {!editing && !user.id && (
          <button
            className="m-6 px-6 py-2 rounded bg-blue-600 text-white hover:bg-blue-900"
            onClick={handleCreateNew}
          >
            Create New Profile
          </button>
        )}
      </div>
    </div>
  );
}
